import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { useGetEventLog } from '../../hooks/api/use-event-log.hook';
import { LoadingIndicator } from '../LoadingIndicator';
import { EventType } from '../../../../types/constants/event-type';
import { OntologyAttributeUpdatedEventDetails } from './OntologyAttributeUpdatedEventDetails';
import { OntologyAttributeUpdatedEvent } from '../../../../types/models/event/ontology.event';
import { convertCamelCaseToUserFriendly } from '../../utils/casing';

export const EventLogDetails = () => {
  const navigate = useNavigate();
  const { eventLogId } = useParams<{ eventLogId: string }>();

  const {
    eventLog,
    loading: loadingEventLog,
    error: gettingError,
  } = useGetEventLog({ eventLogId: Number(eventLogId) });

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError.response?.data?.message}</Alert>;
    } else if (eventLog?.type === EventType.ONTOLOGY_ATTRIBUTE_UPDATED) {
      return (
        <OntologyAttributeUpdatedEventDetails details={eventLog.details as OntologyAttributeUpdatedEvent['details']} />
      );
    }

    return Object.entries(eventLog?.details ?? {})?.map(([detailName, value]) => (
      <Box key={detailName} display="flex" flexDirection={typeof value === 'object' ? 'column' : 'row'}>
        <Typography sx={{ fontWeight: 'bold' }}>{`${convertCamelCaseToUserFriendly(detailName)}:`}</Typography>
        {typeof value === 'object' ? (
          Object.entries(value ?? {})?.map(([objectKey, objectValue]) => (
            <Box key={objectKey} display="flex">
              <Typography
                sx={{ pl: 2, fontWeight: 'bold' }}
              >{`${convertCamelCaseToUserFriendly(objectKey)}:`}</Typography>
              <Box flexGrow={1} />
              <Typography sx={{ pl: 5 }}>{`${objectValue}`}</Typography>
            </Box>
          ))
        ) : (
          <>
            <Box flexGrow={1} />
            <Typography sx={{ pl: 10 }}>{`${value}`}</Typography>
          </>
        )}
      </Box>
    ));
  };

  if (loadingEventLog) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => navigate('..')} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
